import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { gql } from '@soundxyz/gql-string';
import { ShelfTypeFilterEnum } from '@soundxyz/utils/constants/enums';
import { NewPlaylistCta } from '@components/Playlists/NewPlaylistCta';
import { PlaylistPreview } from '@components/Playlists/Preview';
import { PreviewSkeleton } from '@components/Playlists/Preview/Skeleton';
import { PlaylistFilters } from '@components/Playlists/basics/PlaylistFilters';
import { PLAYLISTS_COUNT, shouldShowLikedSounds } from '@components/Playlists/shared';

import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { useAuth } from '@context/auth/base';

import type { FragmentType } from '@graphql/reactQuery';
import {
  AllUserPlaylistsDocument,
  getFragment,
  PlaylistsSectionUserInfoFragmentDoc,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { formatProfileHeader } from '@utils/helpers';
import { SectionHeader } from './SectionHeader';
import { Subheader } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query allUserPlaylists(
    $userId: UUID!
    $shelfPagination: ShelfCursorConnectionArgs!
    $filter: UserShelvesFilter!
  ) {
    user(id: $userId) {
      id
      likedSounds {
        id
        releaseCount
        ...PlaylistPreview
      }
      shelvesCount(filter: $filter)

      shelves(pagination: $shelfPagination, filter: $filter) {
        edges {
          node {
            id
            createdAt
            releaseCount
            ...PlaylistPreview
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment PlaylistsSectionUserInfo on User {
    id
    webappUri
  }
`);

export const PlaylistsSection = ({
  user,
}: {
  user: FragmentType<PlaylistsSectionUserInfoFragmentDoc>;
}) => {
  const { authUser } = useAuth();
  const [playlistsType, setPlaylistsType] = useState<ShelfTypeFilterEnum>(ShelfTypeFilterEnum.ALL);

  const { id: userId, webappUri } = getFragment(PlaylistsSectionUserInfoFragmentDoc, user);

  const isOwner = userId === authUser?.id;

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });

  const {
    orderedList: playlists,
    isLoading: loading,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    firstPage: data,
  } = useInfiniteQuery(AllUserPlaylistsDocument, {
    enabled: !!userId,
    filterQueryKey: {
      playlistsType,
      userId,
    },
    getNextPageParam({ data }) {
      return (
        data.user?.shelves.pageInfo.hasNextPage && { after: data.user.shelves.pageInfo.endCursor }
      );
    },
    variables:
      !!userId &&
      (({ pageParam }) => {
        return {
          userId,
          shelfPagination: {
            first: 20,
            after: pageParam?.after || null,
            sort: {
              createdAt: 'DESC' as const,
            },
          },
          filter: {
            type: playlistsType,
          },
        };
      }),
    list({ user }) {
      if (isOwner) {
        return user?.shelves.edges.map(edge => edge.node);
      }

      return user?.shelves.edges.map(edge => edge.node).filter(node => node.releaseCount > 0);
    },
    uniq({ id }) {
      return id;
    },
    order: [v => v.createdAt, 'desc'],
    staleTime: 0,
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMore();
    }
  }, [isAtBottom, loadMore, hasNextPage]);

  const likedSounds = data?.data.user?.likedSounds;
  const hasLikedSounds = !!likedSounds && likedSounds.releaseCount > 0;
  const COUNT = PLAYLISTS_COUNT({
    type: playlistsType,
    shelfCount: data?.data.user?.shelvesCount ?? 0,
    likedSound: hasLikedSounds,
  });

  const profileHeader = formatProfileHeader({
    count: COUNT,
    text: 'Playlists',
  });

  const subHeader = (
    <div className="flex flex-col gap-2">
      {loading ? <DeprecatedDynamicSkeleton width={100} height={20} /> : profileHeader}
      <PlaylistFilters
        playlistsType={playlistsType}
        onClick={type => {
          setPlaylistsType(type);
        }}
      />
    </div>
  );

  const skeletons = (
    <>
      <PreviewSkeleton />
      <PreviewSkeleton />
    </>
  );

  return (
    <>
      <SectionHeader header="Playlists" userWebappUri={webappUri} />
      <Subheader>{subHeader}</Subheader>
      <div className="mt-6 flex flex-col gap-[65px] px-6 lg:mt-8 lt:px-0">
        {loading ? (
          skeletons
        ) : (
          <>
            {COUNT === 0 ? (
              <NewPlaylistCta isOwner={isOwner} type={playlistsType} />
            ) : (
              <>
                {hasLikedSounds && shouldShowLikedSounds(playlistsType) && (
                  <PlaylistPreview
                    key={likedSounds.id}
                    playlistFrag={likedSounds}
                    source="Profile Playlists"
                  />
                )}
                {playlists.map(shelf => (
                  <PlaylistPreview key={shelf.id} playlistFrag={shelf} source="Profile Playlists" />
                ))}
                <div ref={bottomRef} />
                {hasNextPage && skeletons}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
